import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LoadingSkeleton from './components/LoadingSkeleton';
import BadgeEditModal from './components/BadgeEditModal';
import AssignedUsersList from './components/AssignedUsersList';
import { useAuth } from '../../contexts/AuthContext';
import BadgeService from '../../services/badgeService';

const BadgeDetailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSuperUser } = useAuth();
  const badgeId = searchParams?.get('id');

  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedUsersLoading, setAssignedUsersLoading] = useState(false);

  // Load badge details
  const loadBadgeDetails = async () => {
    if (!badgeId) {
      setError('Badge ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await BadgeService?.getBadgeById(badgeId);
      if (error) {
        setError('Failed to load badge details: ' + error);
      } else {
        setBadge(data);
        setAssignedUsers(data?.user_badges || []);
      }
    } catch (err) {
      setError('Failed to load badge details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle badge update
  const handleBadgeUpdate = async (updatedData) => {
    try {
      const { data, error } = await BadgeService?.updateBadge(badgeId, updatedData);
      if (error) {
        setError('Failed to update badge: ' + error);
        return false;
      }
      
      await loadBadgeDetails();
      return true;
    } catch (err) {
      setError('Failed to update badge. Please try again.');
      return false;
    }
  };

  // Handle badge deletion
  const handleBadgeDelete = async () => {
    if (!confirm('Are you sure you want to delete this badge? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await BadgeService?.deleteBadge(badgeId);
      if (error) {
        setError('Failed to delete badge: ' + error);
        return;
      }
      
      navigate('/all-badges');
    } catch (err) {
      setError('Failed to delete badge. Please try again.');
    }
  };

  // Handle badge status toggle
  const handleStatusToggle = async () => {
    try {
      const { error } = await BadgeService?.toggleBadgeStatus(badgeId, badge?.is_active);
      if (error) {
        setError('Failed to update badge status: ' + error);
        return;
      }
      
      await loadBadgeDetails();
    } catch (err) {
      setError('Failed to update badge status. Please try again.');
    }
  };

  useEffect(() => {
    loadBadgeDetails();
  }, [badgeId]);

  const getBadgeIcon = (category) => {
    switch (category) {
      case 'learning': return 'BookOpen';
      case 'streak': return 'Zap';
      case 'mastery': return 'Star';
      case 'special': return 'Crown';
      default: return 'Award';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'learning': return 'text-blue-400 bg-blue-100';
      case 'streak': return 'text-yellow-600 bg-yellow-100';
      case 'mastery': return 'text-purple-600 bg-purple-100';
      case 'special': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error || !badge) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-8 text-center">
            <Icon name="AlertTriangle" size={48} className="mx-auto text-red-600 mb-4" />
            <h2 className="text-xl font-heading font-semibold text-red-800 mb-2">
              Error Loading Badge
            </h2>
            <p className="text-red-700 mb-6">
              {error || 'The requested badge could not be found.'}
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/all-badges')}
                iconName="ArrowLeft"
                iconPosition="left"
                className="border-red-400 text-red-600 hover:bg-red-400 hover:text-white"
              >
                Back to All Badges
              </Button>
              <Button
                variant="default"
                onClick={loadBadgeDetails}
                iconName="RefreshCw"
                iconPosition="left"
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{badge?.title} - Badge Details - RoleCall</title>
        <meta name="description" content={`View details and assignment statistics for the ${badge?.title} badge.`} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-blue-200 mb-6">
            <button
              onClick={() => navigate('/all-badges')}
              className="hover:text-white transition-colors duration-200"
            >
              All Badges
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-white">{badge?.title}</span>
          </div>

          {/* Badge Header */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-6">
                {/* Badge Icon/Image */}
                <div className="flex-shrink-0">
                  {badge?.image_url ? (
                    <img
                      src={badge?.image_url}
                      alt={badge?.title}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white border-opacity-20"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <Icon 
                        name={getBadgeIcon(badge?.category)} 
                        size={40} 
                        className="text-white" 
                      />
                    </div>
                  )}
                </div>

                {/* Badge Info */}
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-heading font-bold text-white">
                      {badge?.title}
                    </h1>
                    {!badge?.is_active && (
                      <span className="px-3 py-1 bg-red-500 bg-opacity-20 text-red-300 text-sm rounded-lg font-caption">
                        Inactive
                      </span>
                    )}
                  </div>
                  
                  <p className="text-blue-100 text-lg font-body mb-4 leading-relaxed">
                    {badge?.description}
                  </p>

                  <div className="flex items-center space-x-6">
                    <div className={`px-3 py-1 rounded-lg font-caption text-sm ${getCategoryColor(badge?.category)}`}>
                      <Icon name={getBadgeIcon(badge?.category)} size={14} className="inline mr-1" />
                      {badge?.category?.charAt(0)?.toUpperCase() + badge?.category?.slice(1)}
                    </div>
                    
                    <div className="flex items-center space-x-1 text-yellow-300">
                      <Icon name="Star" size={16} />
                      <span className="font-caption">{badge?.points} points</span>
                    </div>
                    
                    <div className="flex items-center space-x-1 text-green-300">
                      <Icon name="Users" size={16} />
                      <span className="font-caption">
                        {badge?.assignment_count || 0} users earned
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {isSuperUser() && (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(true)}
                    iconName="Edit"
                    iconPosition="left"
                    className="border-blue-400 text-blue-300 hover:bg-blue-400 hover:text-white rounded-lg"
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleStatusToggle}
                    iconName={badge?.is_active ? 'Pause' : 'Play'}
                    iconPosition="left"
                    className={`rounded-lg ${badge?.is_active
                      ? 'border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-white' :'border-green-400 text-green-300 hover:bg-green-400 hover:text-white'
                    }`}
                  >
                    {badge?.is_active ? 'Deactivate' : 'Activate'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleBadgeDelete}
                    iconName="Trash2"
                    iconPosition="left"
                    className="border-red-400 text-red-300 hover:bg-red-400 hover:text-white rounded-lg"
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Badge Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="Users" size={24} className="text-green-400" />
                <h3 className="text-lg font-heading font-semibold text-white">Total Earned</h3>
              </div>
              <p className="text-3xl font-bold text-green-300">
                {badge?.assignment_count || 0}
              </p>
              <p className="text-blue-200 text-sm mt-1">Users who earned this badge</p>
            </div>

            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="Calendar" size={24} className="text-blue-400" />
                <h3 className="text-lg font-heading font-semibold text-white">Created</h3>
              </div>
              <p className="text-3xl font-bold text-blue-300">
                {new Date(badge?.created_at)?.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
              <p className="text-blue-200 text-sm mt-1">Badge creation date</p>
            </div>

            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="Activity" size={24} className="text-purple-400" />
                <h3 className="text-lg font-heading font-semibold text-white">Status</h3>
              </div>
              <p className={`text-3xl font-bold ${badge?.is_active ? 'text-green-300' : 'text-red-300'}`}>
                {badge?.is_active ? 'Active' : 'Inactive'}
              </p>
              <p className="text-blue-200 text-sm mt-1">
                {badge?.is_active ? 'Can be earned by users' : 'Cannot be earned'}
              </p>
            </div>
          </div>

          {/* Assigned Users */}
          <AssignedUsersList 
            assignedUsers={assignedUsers}
            loading={assignedUsersLoading}
            badgeTitle={badge?.title}
          />
        </main>

        {/* Edit Modal */}
        {isSuperUser() && (
          <BadgeEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            badge={badge}
            onSave={handleBadgeUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default BadgeDetailPage;